import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import Modal from "@/components/Modal";
import HeirAssignmentPicker from "@/components/HeirAssignmentPicker";
import ExampleHint from "@/components/ExampleHint";
import {
  Plus,
  Landmark,
  Share2,
  Bitcoin,
  Mail,
  Smartphone,
  Shield,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  AlertTriangle,
  Lock,
  LayoutGrid,
  List as ListIcon,
} from "lucide-react";

const CATEGORIES = [
  {
    key: "bank",
    label: "Cuentas bancarias",
    newLabel: "Nueva cuenta bancaria",
    icon: Landmark,
    emoji: "🏦",
    fields: [
      { name: "bank", label: "Banco", required: true, type: "text" },
      { name: "iban", label: "Número de cuenta / IBAN", required: true, type: "text", mono: true },
      { name: "username", label: "Usuario online", type: "text" },
      { name: "credentials_location", label: "¿Dónde están las credenciales?", type: "text", hint: "vault_hint" },
      { name: "notes", label: "Notas", type: "textarea" },
    ],
  },
  {
    key: "social",
    label: "Redes sociales",
    newLabel: "Nueva red social",
    icon: Share2,
    emoji: "📱",
    fields: [
      {
        name: "network",
        label: "Red social",
        required: true,
        type: "select",
        options: ["Instagram", "Facebook", "TikTok", "Twitter / X", "LinkedIn", "YouTube", "Snapchat", "Otros"],
      },
      { name: "username", label: "Usuario", required: true, type: "text" },
      { name: "credentials_location", label: "¿Dónde están las credenciales?", type: "text", hint: "vault_hint" },
      { name: "account_email", label: "Email de la cuenta", type: "email" },
    ],
    instructionLabel: "Instrucción",
    instructionOptions: ["Eliminar cuenta", "Convertir en memorial", "Entregar acceso"],
  },
  {
    key: "crypto",
    label: "Criptomonedas",
    newLabel: "Nueva cuenta de criptomonedas",
    icon: Bitcoin,
    emoji: "₿",
    warning: true,
    fields: [
      {
        name: "platform",
        label: "Exchange o Wallet",
        required: true,
        type: "select",
        options: ["Binance", "Coinbase", "Kraken", "MetaMask", "Ledger", "Trezor", "Trust Wallet", "Otros"],
      },
      { name: "email", label: "Email", type: "email" },
      { name: "credentials_location", label: "¿Dónde están las credenciales y la seed phrase?", type: "textarea", hint: "vault_hint_crypto" },
    ],
    instructionLabel: "Instrucción para el heredero",
  },
  {
    key: "email",
    label: "Email principal",
    newLabel: "Nueva cuenta de email",
    icon: Mail,
    emoji: "📧",
    fields: [
      {
        name: "provider",
        label: "Proveedor",
        required: true,
        type: "select",
        options: ["Gmail", "Outlook", "Yahoo", "iCloud", "ProtonMail", "Otros"],
      },
      { name: "email", label: "Email", required: true, type: "email" },
      { name: "credentials_location", label: "¿Dónde están las credenciales?", type: "text", hint: "vault_hint" },
      { name: "notes", label: "Notas", type: "textarea" },
    ],
  },
  {
    key: "device",
    label: "Dispositivos",
    newLabel: "Nuevo dispositivo",
    icon: Smartphone,
    emoji: "📱",
    fields: [
      {
        name: "device",
        label: "Dispositivo",
        required: true,
        type: "select",
        options: ["iPhone", "Android", "Mac", "PC Windows", "iPad", "Tablet", "Otros"],
      },
      { name: "credentials_location", label: "¿Dónde está el PIN / contraseña del dispositivo?", type: "text", hint: "vault_hint" },
      { name: "account", label: "Apple ID o Google Account", type: "email" },
      { name: "notes", label: "Notas", type: "textarea" },
    ],
  },
  {
    key: "insurance",
    label: "Seguros y pensiones",
    newLabel: "Nuevo seguro / pensión",
    icon: Shield,
    emoji: "📋",
    fields: [
      { name: "company", label: "Compañía", required: true, type: "text" },
      { name: "policy_number", label: "Número de póliza", required: true, type: "text", mono: true },
      { name: "contact_phone", label: "Teléfono de contacto", type: "text" },
      {
        name: "beneficiary_designated",
        label: "Beneficiario ya designado",
        type: "select",
        options: ["Sí", "No", "No lo sé"],
      },
      { name: "notes", label: "Notas", type: "textarea" },
    ],
  },
];

// Localised label dictionary. Looked up at render time. If a Spanish label has no
// translation entry, we fall back to the Spanish source (used by the ES UI).
const DA_LABELS = {
  en: {
    "Cuentas bancarias": "Bank accounts",
    "Nueva cuenta bancaria": "New bank account",
    "Banco": "Bank",
    "Número de cuenta / IBAN": "Account number / IBAN",
    "Usuario online": "Online username",
    "Contraseña": "Password",
    "Notas": "Notes",
    "Redes sociales": "Social media",
    "Nueva red social": "New social media",
    "Red social": "Social network",
    "Usuario": "Username",
    "Email de la cuenta": "Account email",
    "Instrucción": "Instruction",
    "Eliminar cuenta": "Delete account",
    "Convertir en memorial": "Convert to memorial",
    "Entregar acceso": "Hand over access",
    "Criptomonedas": "Cryptocurrencies",
    "Nueva cuenta de criptomonedas": "New crypto account",
    "Exchange o Wallet": "Exchange or Wallet",
    "Email": "Email",
    "Seed phrase / clave privada": "Seed phrase / private key",
    "Instrucción para el heredero": "Instruction for the heir",
    "Email principal": "Main email",
    "Nueva cuenta de email": "New email account",
    "Proveedor": "Provider",
    "Dispositivos": "Devices",
    "Nuevo dispositivo": "New device",
    "Dispositivo": "Device",
    "PIN o contraseña": "PIN or password",
    "Apple ID o Google Account": "Apple ID or Google Account",
    "Seguros y pensiones": "Insurance and pensions",
    "Nuevo seguro / pensión": "New insurance / pension",
    "Compañía": "Company",
    "Número de póliza": "Policy number",
    "Teléfono de contacto": "Contact phone",
    "Beneficiario ya designado": "Beneficiary already designated",
    "Sí": "Yes",
    "No": "No",
    "No lo sé": "I don't know",
    "Otros": "Others",
    "¿Dónde están las credenciales?": "Where are the credentials?",
    "¿Dónde están las credenciales y la seed phrase?": "Where are the credentials and seed phrase?",
    "¿Dónde está el PIN / contraseña del dispositivo?": "Where is the device PIN / password?",
  },
  zh: {
    "Cuentas bancarias": "银行账户",
    "Nueva cuenta bancaria": "新银行账户",
    "Banco": "银行",
    "Número de cuenta / IBAN": "账号 / IBAN",
    "Usuario online": "在线用户名",
    "Contraseña": "密码",
    "Notas": "备注",
    "Redes sociales": "社交媒体",
    "Nueva red social": "新增社交账户",
    "Red social": "社交平台",
    "Usuario": "用户名",
    "Email de la cuenta": "账户邮箱",
    "Instrucción": "处理指示",
    "Eliminar cuenta": "删除账户",
    "Convertir en memorial": "转为纪念账户",
    "Entregar acceso": "交付访问权限",
    "Criptomonedas": "加密货币",
    "Nueva cuenta de criptomonedas": "新增加密货币账户",
    "Exchange o Wallet": "交易所或钱包",
    "Email": "邮箱",
    "Seed phrase / clave privada": "助记词 / 私钥",
    "Instrucción para el heredero": "给继承人的指示",
    "Email principal": "主邮箱",
    "Nueva cuenta de email": "新邮箱账户",
    "Proveedor": "提供商",
    "Dispositivos": "设备",
    "Nuevo dispositivo": "新设备",
    "Dispositivo": "设备",
    "PIN o contraseña": "PIN 码或密码",
    "Apple ID o Google Account": "Apple ID 或 Google 账户",
    "Seguros y pensiones": "保险与养老金",
    "Nuevo seguro / pensión": "新保险 / 养老金",
    "Compañía": "公司",
    "Número de póliza": "保单号",
    "Teléfono de contacto": "联系电话",
    "Beneficiario ya designado": "已指定受益人",
    "Sí": "是",
    "No": "否",
    "No lo sé": "不确定",
    "Otros": "其他",
    "¿Dónde están las credenciales?": "凭证存放在哪里?",
    "¿Dónde están las credenciales y la seed phrase?": "凭证和助记词存放在哪里?",
    "¿Dónde está el PIN / contraseña del dispositivo?": "设备的 PIN 或密码存放在哪里?",
  },
};

export default function DigitalAssets() {
  const { t, lang } = useI18n();
  // Local translator for the embedded CATEGORIES dictionary. Falls back to the
  // Spanish source label if no translation entry exists for the current language.
  const tl = useCallback(
    (s) => (DA_LABELS[lang] && DA_LABELS[lang][s]) || s,
    [lang]
  );
  // Privacy: never record session replay where seed phrases / bank creds appear.
  useEffect(() => {
    try { window.__ksTrackingPause && window.__ksTrackingPause(); } catch {}
    return () => { try { window.__ksTrackingResume && window.__ksTrackingResume(); } catch {} };
  }, []);
  const [activeCat, setActiveCat] = useState("bank");
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("ks_da_view") || "grid"; } catch { return "grid"; }
  });
  const [assets, setAssets] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ category: "bank", title: "", fields: {}, instruction: "", notes: "", assigned_heirs: [] });
  const [reveal, setReveal] = useState({});
  const [heirsMap, setHeirsMap] = useState({});

  const setViewModePersisted = (m) => {
    setViewMode(m);
    try { localStorage.setItem("ks_da_view", m); } catch { /* ignore quota errors */ }
  };

  const load = useCallback(async () => {
    const [r, h] = await Promise.all([
      api.get("/digital-assets"),
      api.get("/heirs").catch(() => ({ data: [] })),
    ]);
    setAssets(r.data);
    const m = {};
    (h.data || []).forEach((x) => { m[x.heir_id] = x.name; });
    setHeirsMap(m);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const cat = CATEGORIES.find((c) => c.key === activeCat);
  const filtered = assets.filter((a) => a.category === activeCat);

  const openCreate = () => {
    setEditingId(null);
    setForm({ category: activeCat, title: "", fields: {}, instruction: "", notes: "", assigned_heirs: [] });
    setReveal({});
    setOpen(true);
  };

  const openEdit = (a) => {
    setEditingId(a.asset_id);
    setForm({
      category: a.category,
      title: a.title,
      fields: a.fields || {},
      instruction: a.instruction || "",
      notes: a.notes || "",
      assigned_heirs: a.assigned_heirs || [],
    });
    setReveal({});
    setViewing(null);
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const r = await api.put(`/digital-assets/${editingId}`, form);
        setAssets((arr) => arr.map((x) => (x.asset_id === editingId ? r.data : x)));
      } else {
        const r = await api.post("/digital-assets", form);
        setAssets((arr) => [...arr, r.data]);
      }
      toast.success(t("saved"));
      setOpen(false);
      setEditingId(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/digital-assets/${id}`);
    toast.success(t("deleted"));
    setAssets((arr) => arr.filter((x) => x.asset_id !== id));
    if (viewing?.asset_id === id) setViewing(null);
  };

  return (
    <div data-testid="digital-assets-page">
      <PageHeader
        title={t("set_assets_card_title")}
        subtitle={t("da_subtitle")}
        action={
          <div
            className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 w-full sm:w-auto"
            data-testid="da-header-actions"
          >
            {/* View-mode toggle — its own row on mobile, sits left of Add on desktop */}
            <div
              className="flex items-center rounded-md border border-gold-subtle overflow-hidden self-start sm:self-auto"
              data-testid="da-view-toggle"
            >
              <button
                type="button"
                onClick={() => setViewModePersisted("grid")}
                className={`px-3 py-2.5 text-xs flex items-center gap-1.5 transition ${
                  viewMode === "grid"
                    ? "bg-gold/10 text-gold-light"
                    : "text-muted hover:text-gold-light"
                }`}
                aria-pressed={viewMode === "grid"}
                aria-label={t("da_view_grid")}
                data-testid="da-view-grid"
              >
                <LayoutGrid className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setViewModePersisted("list")}
                className={`px-3 py-2.5 text-xs flex items-center gap-1.5 transition border-l border-gold-subtle ${
                  viewMode === "list"
                    ? "bg-gold/10 text-gold-light"
                    : "text-muted hover:text-gold-light"
                }`}
                aria-pressed={viewMode === "list"}
                aria-label={t("da_view_list")}
                data-testid="da-view-list"
              >
                <ListIcon className="w-3.5 h-3.5" />
              </button>
            </div>
            <button
              className="btn-gold w-full sm:w-auto justify-center whitespace-nowrap"
              onClick={openCreate}
              data-testid="asset-add-btn"
            >
              <Plus className="w-4 h-4" /> {t("da_add")} {cat?.emoji} {tl(cat?.label)}
            </button>
          </div>
        }
      />

      {viewMode === "list" ? (
        /* ============ LIST VIEW ============ */
        assets.length === 0 ? (
          <EmptyState>
            <span className="text-3xl block mb-2">📋</span>
            {t("da_list_empty")}
          </EmptyState>
        ) : (
          <div
            className="glass-card rounded-md overflow-hidden divide-y divide-gold-subtle"
            data-testid="da-list-container"
          >
            {/* Header row (hidden on mobile) */}
            <div className="hidden sm:grid grid-cols-12 gap-3 px-4 py-2.5 text-[10px] uppercase tracking-wider text-faded font-label bg-white/[0.02]">
              <div className="col-span-5">{t("da_list_col_name")}</div>
              <div className="col-span-3">{t("da_list_col_category")}</div>
              <div className="col-span-4">{t("da_list_col_heir")}</div>
            </div>
            {assets.map((a) => {
              const acat = CATEGORIES.find((c) => c.key === a.category);
              const heirNames = (a.assigned_heirs || [])
                .map((id) => heirsMap[id])
                .filter(Boolean);
              return (
                <button
                  key={a.asset_id}
                  onClick={() => {
                    setViewing(a);
                    setReveal({});
                  }}
                  className="w-full text-left hover:bg-gold/5 transition"
                  data-testid={`da-list-row-${a.asset_id}`}
                >
                  {/* On mobile each metadata row is its own line and wraps;
                      on desktop we keep the 12-col grid for a clean table. */}
                  <div className="flex flex-col sm:grid sm:grid-cols-12 sm:gap-3 px-4 py-3 sm:items-center gap-1.5">
                    <div className="sm:col-span-5 flex items-center gap-3 min-w-0">
                      <span className="text-lg shrink-0" aria-hidden>{acat?.emoji}</span>
                      <span className="text-gold-light break-words">{a.title}</span>
                    </div>
                    <div className="sm:col-span-3 text-xs text-muted break-words">
                      <span className="sm:hidden text-faded font-label mr-1">{t("da_list_col_category")}: </span>
                      {tl(acat?.label)}
                    </div>
                    <div className="sm:col-span-4 text-xs break-words">
                      <span className="sm:hidden text-faded font-label mr-1">{t("da_list_col_heir")}: </span>
                      {heirNames.length > 0 ? (
                        <span className="text-emerald-400/90">{heirNames.join(", ")}</span>
                      ) : (
                        <span className="text-yellow-500/80">— {t("da_list_no_heir")}</span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )
      ) : (
        <>
      {/* Category tabs — horizontally scrollable with a right-side fade hint */}
      <div className="ks-tab-scroller relative mb-6">
        <div className="ks-tab-list flex overflow-x-auto gap-2 pb-2 -mx-1 px-1 snap-x">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const count = assets.filter((a) => a.category === c.key).length;
            const active = c.key === activeCat;
            return (
              <button
                key={c.key}
                onClick={() => setActiveCat(c.key)}
                data-testid={`cat-tab-${c.key}`}
                className={`shrink-0 snap-start flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-md text-xs sm:text-sm transition border ${
                  active
                    ? "bg-gold/10 border-gold text-gold-light"
                    : "border-gold-subtle text-muted hover:text-gold-light hover:border-gold"
                }`}
              >
                <span className="text-base">{c.emoji}</span>
                <span className="whitespace-nowrap">{tl(c.label)}</span>
                {count > 0 && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${active ? "bg-gold text-obsidian" : "bg-gold-subtle text-gold"}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="ks-tab-fade" aria-hidden="true" />
      </div>

      {filtered.length === 0 ? (
        <EmptyState>
          <span className="text-3xl block mb-2">{cat?.emoji}</span>
          {t("da_empty_for").replace("{LABEL}", tl(cat?.label).toLowerCase())}
        </EmptyState>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((a) => (
            <button
              key={a.asset_id}
              onClick={() => {
                setViewing(a);
                setReveal({});
              }}
              className="glass-card rounded-md p-5 text-left hover:border-gold transition"
              data-testid={`asset-${a.asset_id}`}
            >
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-md bg-gold/10 border border-gold-subtle">
                  <span className="text-xl">{cat.emoji}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-gold-light truncate">{a.title}</div>
                  <div className="text-xs text-faded mt-1 flex flex-wrap gap-2 items-center">
                    {a.assigned_heirs?.length > 0 ? (
                      <span className="text-emerald-400/90">
                        ✓ {t("da_assigned_to")} {a.assigned_heirs.length} {a.assigned_heirs.length === 1 ? t("da_heir") : t("da_heirs")}
                      </span>
                    ) : (
                      <span className="text-yellow-500/90">⚠️ {t("da_no_heirs_assigned")}</span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
        </>
      )}

      <ExampleHint slug="assets" testid="digital-assets-hint" />

      {/* VIEW MODAL */}
      <Modal
        open={!!viewing && !open}
        onClose={() => setViewing(null)}
        title={viewing?.title || ""}
        testid="asset-view-modal"
      >
        {viewing && (() => {
          const vcat = CATEGORIES.find((c) => c.key === viewing.category);
          return (
            <div className="space-y-4">
              <div className="font-label text-gold flex items-center gap-2">
                {vcat?.emoji} {tl(vcat?.label)}
              </div>
              {vcat?.fields.map((f) => {
                const val = viewing.fields?.[f.name];
                if (!val) return null;
                if (f.sensitive) {
                  return (
                    <div key={f.name}>
                      <div className="font-label text-faded mb-1.5">{tl(f.label)}</div>
                      <div className="flex items-center gap-2">
                        <input
                          readOnly
                          type={reveal[f.name] ? "text" : "password"}
                          value={val}
                          className={`input-field flex-1 ${f.mono ? "font-mono text-sm" : ""}`}
                        />
                        <button
                          type="button"
                          onClick={() => setReveal((r) => ({ ...r, [f.name]: !r[f.name] }))}
                          className="btn-ghost px-3"
                        >
                          {reveal[f.name] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={f.name}>
                    <div className="font-label text-faded mb-1">{tl(f.label)}</div>
                    <div className={`text-gold-light break-all ${f.mono ? "font-mono text-sm" : ""}`}>{val}</div>
                  </div>
                );
              })}
              {/* Legacy fields — entries that were saved before the schema
                  dropped password/pin/seed_phrase. We still show them as
                  read-only (with the eye toggle) so the user does not lose
                  data, but the editor never asks for them again. */}
              {Object.entries(viewing.fields || {})
                .filter(([k, v]) => v && !(vcat?.fields || []).some((f) => f.name === k))
                .map(([k, v]) => {
                  const isSecret = ["password", "pin", "seed_phrase"].includes(k);
                  return (
                    <div key={`legacy-${k}`} data-testid={`asset-legacy-${k}`}>
                      <div className="font-label text-faded mb-1.5 flex items-center gap-2">
                        <span>{k === "password" ? t("da_legacy_password") : k === "pin" ? t("da_legacy_pin") : k === "seed_phrase" ? t("da_legacy_seed") : k}</span>
                        <span className="text-[10px] uppercase tracking-wider text-amber-400">
                          {t("da_legacy_tag")}
                        </span>
                      </div>
                      {isSecret ? (
                        <div className="flex items-center gap-2">
                          <input
                            readOnly
                            type={reveal[k] ? "text" : "password"}
                            value={v}
                            className="input-field flex-1 font-mono text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => setReveal((r) => ({ ...r, [k]: !r[k] }))}
                            className="btn-ghost px-3"
                          >
                            {reveal[k] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      ) : (
                        <div className="text-gold-light break-all">{v}</div>
                      )}
                    </div>
                  );
                })}
              {viewing.instruction && (
                <div>
                  <div className="font-label text-faded mb-1">{vcat?.instructionLabel ? tl(vcat.instructionLabel) : t("da_view_instruction")}</div>
                  <div className="text-gold-light text-sm">{viewing.instruction}</div>
                </div>
              )}
              {viewing.notes && (
                <div>
                  <div className="font-label text-faded mb-1">{t("da_view_notes")}</div>
                  <div className="text-muted whitespace-pre-wrap text-sm">{viewing.notes}</div>
                </div>
              )}

              <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-md p-3 text-xs text-emerald-400/90 flex gap-2">
                <Lock className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>{t("da_view_encryption")}</span>
              </div>

              <div className="flex gap-3 justify-end pt-3 border-t border-gold-subtle">
                <button className="btn-ghost" onClick={() => remove(viewing.asset_id)} data-testid="asset-view-delete">
                  <Trash2 className="w-4 h-4" /> {t("delete")}
                </button>
                <button className="btn-gold" onClick={() => openEdit(viewing)} data-testid="asset-view-edit">
                  <Pencil className="w-4 h-4" /> {t("edit")}
                </button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* CREATE/EDIT MODAL */}
      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setEditingId(null);
        }}
        title={editingId ? t("da_edit_asset") : (tl(cat?.newLabel) || `${t("da_add")} ${tl(cat?.label).toLowerCase()}`)}
        testid="asset-modal"
      >
        <form onSubmit={save} className="space-y-4">
          {cat?.warning && (
            <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 flex items-start gap-2 text-xs text-red-200">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                ⚠️ {t("da_warn_sensitive")}
              </span>
            </div>
          )}

          <div>
            <label className="font-label text-faded block mb-2">{t("da_label_title")}</label>
            <input
              className="input-field"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder={t("da_title_ph")
                .replace("{EXAMPLE}", cat?.key === "bank" ? t("da_eg_bank")
                  : cat?.key === "social" ? t("da_eg_social")
                  : cat?.key === "crypto" ? "Binance"
                  : t("da_eg_generic"))}
              data-testid="asset-title"
            />
          </div>

          {cat?.fields.map((f) => {
            const val = form.fields[f.name] || "";
            const setVal = (v) => setForm({ ...form, fields: { ...form.fields, [f.name]: v } });
            if (f.type === "select") {
              return (
                <div key={f.name}>
                  <label className="font-label text-faded block mb-2">{tl(f.label)}</label>
                  <select className="input-field" required={f.required} value={val} onChange={(e) => setVal(e.target.value)}>
                    <option value="">— {t("select")} —</option>
                    {f.options.map((o) => (
                      <option key={o} value={o}>{tl(o)}</option>
                    ))}
                  </select>
                </div>
              );
            }
            if (f.type === "textarea") {
              return (
                <div key={f.name}>
                  <label className="font-label text-faded block mb-2">
                    {tl(f.label)} {!f.required && <span className="text-faded">({t("optional")})</span>}
                  </label>
                  <textarea
                    className={`input-field ${f.mono ? "font-mono text-sm" : ""}`}
                    rows={f.extraWarning ? 3 : 2}
                    value={val}
                    onChange={(e) => setVal(e.target.value)}
                    autoComplete="off"
                  />
                  {f.extraWarning && (
                    <p className="text-[11px] text-red-300/80 mt-1.5">
                      🔒 {t("da_warn_seed")}
                    </p>
                  )}
                </div>
              );
            }
            if (f.type === "password") {
              return (
                <div key={f.name}>
                  <label className="font-label text-faded block mb-2">{tl(f.label)}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type={reveal[f.name] ? "text" : "password"}
                      className={`input-field flex-1 ${f.mono ? "font-mono" : ""}`}
                      required={f.required}
                      value={val}
                      onChange={(e) => setVal(e.target.value)}
                      autoComplete="new-password"
                    />
                    <button type="button" className="btn-ghost px-3" onClick={() => setReveal((r) => ({ ...r, [f.name]: !r[f.name] }))}>
                      {reveal[f.name] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              );
            }
            return (
              <div key={f.name}>
                <label className="font-label text-faded block mb-2">
                  {tl(f.label)} {!f.required && <span className="text-faded">({t("optional")})</span>}
                </label>
                <input
                  type={f.type === "email" ? "email" : "text"}
                  className={`input-field ${f.mono ? "font-mono text-sm" : ""}`}
                  required={f.required}
                  value={val}
                  onChange={(e) => setVal(e.target.value)}
                  autoComplete="off"
                  placeholder={f.name === "credentials_location" ? t("da_cred_placeholder") : undefined}
                  data-testid={f.name === "credentials_location" ? "da-credentials-location" : undefined}
                />
                {f.hint === "vault_hint" && (
                  <p className="text-[11px] text-faded mt-1.5">
                    🔒 {t("da_cred_hint")}{" "}
                    <Link to="/vault" className="text-gold underline">
                      {t("da_cred_hint_link")}
                    </Link>
                  </p>
                )}
                {f.hint === "vault_hint_crypto" && (
                  <p className="text-[11px] text-red-300/80 mt-1.5">
                    🔒 {t("da_cred_hint_crypto")}{" "}
                    <Link to="/vault" className="text-gold underline">
                      {t("da_cred_hint_link")}
                    </Link>
                  </p>
                )}
              </div>
            );
          })}

          {/* Instruction (for social / crypto) */}
          {cat?.instructionLabel && (
            <div>
              <label className="font-label text-faded block mb-2">{tl(cat.instructionLabel)}</label>
              {cat.instructionOptions ? (
                <select className="input-field" value={form.instruction} onChange={(e) => setForm({ ...form, instruction: e.target.value })}>
                  <option value="">— {t("select")} —</option>
                  {cat.instructionOptions.map((o) => (
                    <option key={o} value={o}>{tl(o)}</option>
                  ))}
                </select>
              ) : (
                <textarea
                  className="input-field"
                  rows={2}
                  value={form.instruction}
                  onChange={(e) => setForm({ ...form, instruction: e.target.value })}
                  placeholder={t("da_instruction_ph")}
                />
              )}
            </div>
          )}

          <div className="divider-gold" />

          <HeirAssignmentPicker
            value={form.assigned_heirs}
            onChange={(arr) => setForm({ ...form, assigned_heirs: arr })}
            label={t("da_assign_heirs")}
          />

          {/* Security warning */}
          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-md p-3 text-xs text-emerald-300/90 flex gap-2">
            <Lock className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              🔒 {t("da_security_note")}
            </span>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>
              {t("cancel")}
            </button>
            <button type="submit" className="btn-gold" data-testid="asset-save">
              {t("save")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
